import {
  BALANCE,
  BUILDABLE_MODULES,
  MODULES,
  buildPrice,
  isUnlocked,
  moduleLimit,
  moduleStats,
  nextRingCost,
  ownedCount,
  ringLevel,
  type BuildableModuleType,
  type StationState,
} from "@station-keeping/sim";
import { play } from "../../game/commands";
import { useGameUi } from "../../game/store";
import { formatNumber, t } from "../../i18n";
import { moduleDescription, moduleName } from "../labels";
import { Icon, PixelText } from "../pixel";
import { Key, Led, Plate, SectionTitle } from "../primitives";
import { ModuleThumb } from "../thumbs";

/** Limite d'un type à ce niveau, et la prochaine fois qu'elle monte (`null` si le type n'a pas de limite). */
function limitInfo(state: StationState, type: BuildableModuleType): { count: number; limit: number; next: { level: number; limit: number } | null } | null {
  const limit = moduleLimit(state.level, type);
  if (limit === null) return null;
  let next: { level: number; limit: number } | null = null;
  for (let level = state.level + 1; level <= BALANCE.progression.maxLevel; level++) {
    const higher = moduleLimit(level, type) ?? 0;
    if (higher > limit) {
      next = { level, limit: higher };
      break;
    }
  }
  return { count: ownedCount(state, type), limit, next };
}

/**
 * Le menu de construction : chaque bâtiment, son prix du moment, sa zone et ce qu'il consomme. Ceux que le
 * niveau ne permet pas encore restent visibles, en bas, avec le niveau qui les débloque.
 */
export function BuildPanel({ state, onPick }: { state: StationState; onPick?: (() => void) | undefined }) {
  const startBuild = useGameUi((ui) => ui.startBuild);
  const openPanel = useGameUi((ui) => ui.openPanel);
  const buildType = useGameUi((ui) => ui.buildType);
  const ringCost = nextRingCost(state);
  const nextRing = state.openRings + 1;
  const ringNeeds = ringCost === null ? 0 : ringLevel(nextRing);
  const types = [
    ...BUILDABLE_MODULES.filter((type) => isUnlocked(state.level, type)),
    ...BUILDABLE_MODULES.filter((type) => !isUnlocked(state.level, type)).sort(
      (a, b) => MODULES[a].unlockLevel - MODULES[b].unlockLevel,
    ),
  ];

  return (
    <div>
      <Plate title={t("build.title")} onClose={() => openPanel(null)}>
        <p className="mt-1.5 text-[0.9375rem] text-muted">{t("build.hint")}</p>
      </Plate>
      <ul>
        {types.map((type) => {
          const locked = !isUnlocked(state.level, type);
          const limit = limitInfo(state, type);
          const full = !locked && limit !== null && limit.count >= limit.limit;
          const price = buildPrice(state, type);
          const balance = moduleStats({ type, level: 1 });
          const affordable = state.credits >= price;
          const selected = buildType === type;
          const unlockLevel = MODULES[type].unlockLevel;
          return (
            <li key={type} className="dotted">
              <button
                type="button"
                onClick={() => {
                  startBuild(type);
                  onPick?.();
                }}
                disabled={locked || full}
                aria-pressed={selected}
                data-tutorial={`build-${type}`}
                className={`flex w-full items-center gap-3 px-1 py-2.5 text-left enabled:hover:bg-face-2 focus-visible:bg-face-2 disabled:cursor-not-allowed ${selected ? "bg-face-2" : ""}`}
              >
                <span className={`grid w-[4.25rem] shrink-0 place-items-center ${locked ? "opacity-40 grayscale" : ""}`}>
                  <ModuleThumb type={type} size="sm" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span className={`flex items-center gap-2 text-base font-semibold ${locked ? "text-dim" : "text-ink"}`}>
                      {selected ? <Led on blink color="var(--color-accent)" /> : null}
                      {moduleName(type)}
                    </span>
                    {locked ? (
                      <span className="flex items-center gap-1.5 text-accent">
                        <Icon name="level" />
                        <PixelText text={t("build.locked", { level: unlockLevel })} />
                      </span>
                    ) : (
                      <span className={affordable && !full ? "text-ink" : "text-stop"}>
                        <PixelText text={`${formatNumber(price)} CR`} label={t("build.price", { amount: price })} />
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-[0.875rem] leading-snug text-muted">
                    {locked ? t("build.lockedHint", { level: unlockLevel }) : moduleDescription(type)}
                  </span>
                  {locked ? null : (
                    <span className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[0.8125rem] text-dim">
                      <span>{t(`zone.${MODULES[type].zone}`)}</span>
                      {balance.power !== 0 ? (
                        <span className={balance.power > 0 ? "text-sun" : ""}>
                          {t("build.power", { value: `${balance.power > 0 ? "+" : "−"}${formatNumber(Math.abs(balance.power))}` })}
                        </span>
                      ) : null}
                      {balance.crew > 0 ? <span>{t("build.crew", { value: balance.crew })}</span> : null}
                      {limit ? (
                        <span
                          className={full ? "text-warn" : ""}
                          title={
                            limit.next
                              ? t("build.limitHint", { limit: limit.limit, next: limit.next.limit, level: limit.next.level })
                              : t("build.limitMax", { limit: limit.limit })
                          }
                        >
                          <PixelText
                            text={t("build.limit", { count: limit.count, limit: limit.limit })}
                            label={
                              limit.next
                                ? t("build.limitHint", { limit: limit.limit, next: limit.next.limit, level: limit.next.level })
                                : t("build.limitMax", { limit: limit.limit })
                            }
                          />
                        </span>
                      ) : null}
                    </span>
                  )}
                  {full && limit?.next ? (
                    <span className="mt-1 block text-[0.8125rem] text-warn">
                      {t("build.limitHint", { limit: limit.limit, next: limit.next.limit, level: limit.next.level })}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <SectionTitle hint={t("build.ringsHint")}>{t("build.ringsTitle")}</SectionTitle>
      {ringCost === null ? (
        <p className="text-[0.9375rem] text-muted">{t("build.allRingsOpen")}</p>
      ) : state.level < ringNeeds ? (
        <p className="flex items-center gap-2 text-[0.9375rem] text-muted">
          <Icon name="level" className="text-accent" />
          {t("build.ringLocked", { ring: nextRing, level: ringNeeds })}
        </p>
      ) : (
        <Key className="w-full justify-between" disabled={state.credits < ringCost} onClick={() => play({ type: "EXTEND_RING" })}>
          <span className="flex items-center gap-2">
            <Icon name="ring" />
            {t("action.extendRing", { ring: nextRing })}
          </span>
          <PixelText text={`${formatNumber(ringCost)} CR`} label={t("action.cost", { amount: ringCost })} />
        </Key>
      )}
    </div>
  );
}
